<script setup>
// import usei18n from '@/core/util/composable/usei18n';
import {computed, reactive, ref, defineProps, inject, onMounted} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {navigate} from '@/core/util/helper/navigation';
import Status from '../constants/Status';
import Spinner from '@ohrm/oxd/core/components/Loader/Spinner';

const props = defineProps({
  showPaginator: {type: Boolean, default: true},
});

const defaultFilters = {
  code: '',
  title: '',
  owners: [],
  createdAt: '',
  dueDate: '',
  status: null,
};

let filters = reactive({...defaultFilters});

const normalizeFilters = ref({...defaultFilters});

const selected = ref(0);
const checkedItems = ref([]);

const http = new APIService(
  window.appGlobal.baseUrl,
  `api/training/information`,
);

const informationNormalizer = data => {
  return data.map(item => {
    return {
      id: item.id,
      code: `INF-${item.id}`,
      title: item.title,
      //version: item.version,
      owner: item.owner,
      ownerId: item.owner_id,
      createdAt: item.created_at,
      dueDate: item.due_date,
      status: Status.filter(status => status.id === item.status)[0].name,
      total: item.total,
    };
  });
};

const {
  showPaginator,
  currentPage,
  total,
  pages,
  pageSize,
  response,
  isLoading,
  execQuery,
} = usePaginate(http, {
  normalizer: informationNormalizer,
  query: normalizeFilters,
  pageSize: 15,
  toastNoRecords: false,
});

const deleteDialog = ref(null);

const deleteItems = items => {
  if (items instanceof Array) {
    http
      .deleteAll({
        ids: items,
      })
      .then(() => {
        // return $toast.deleteSuccess();
      })
      .finally(() => {
        execQuery();
      });
  }
};

const onClickDelete = item => {
  deleteDialog.value.showDialog().then(confirmation => {
    if (confirmation === 'ok') {
      deleteItems([item.id]);
    }
  });
};

const onClickEdit = item => {
  window.location.assign(
    `${window.appGlobal.baseUrl}/training/information/${item.id}`,
  );
};

const onClickView = item => {
  // window.location = `${window.appGlobal.baseUrl}/training/viewInformation/2`
  // navigate(`${window.appGlobal.baseUrl}/training/viewInformation/{id}`, {id: item.id});
  navigate('/training/viewInformation/{id}', {id: item.id});
};

const structuredClone = object => {
  return JSON.parse(JSON.stringify(object));
};

const onReset = () => {
  filters = {...defaultFilters};
  normalizeFilters.value = structuredClone(filters);

  execQuery();
};

const goToCreate = () => {
  window.location = `${window.appGlobal.baseUrl}/training/information`;
};

const searchWithFilters = () => {
  normalizeFilters.value = structuredClone(filters);
  normalizeFilters.value.owners = filters.owners.map(item => item.id);

  if (filters.status?.id) normalizeFilters.value.status = filters.status.id;

  execQuery();
};

const isEmployee = computed(() => {
  return window.appGlobal.user.roleId == 2;
});

const generateActions = (...args) => {
  const [index, , , row] = args;

  console.log(index, row);

  const actions =
    row.ownerId === window.appGlobal.user.id ||
    window.appGlobal.user.roleId === 1
      ? {
          edit: {
            onClick: onClickEdit,
            props: {
              name: 'pencil-fill',
            },
          },
          view: {
            onClick: onClickView,
            props: {
              name: 'eye',
            },
          },
          delete: {
            onClick: onClickDelete,
            component: 'oxd-icon-button',
            props: {
              name: 'trash',
            },
          },
        }
      : {
          view: {
            onClick: onClickView,
            props: {
              name: 'eye',
            },
          },
        };

  return {
    props: {
      header: {
        cellConfig: actions,
      },
    },
  };
};

const headers = ref([
  {name: 'code', title: 'Code', style: {flex: 1}},
  {name: 'title', title: 'Title', style: {flex: 1}},
  // { name: 'version', title: 'Version', style: { flex: 1 } },
  {name: 'owner', title: 'Owner', style: {flex: 1}},
  {name: 'createdAt', title: 'Created at', style: {flex: 1}},
  {name: 'dueDate', title: 'Due Date', style: {flex: 1}},
  {
    name: 'status',
    title: 'Status',
    style: {flex: 1},
  },
  !isEmployee.value && {
    name: 'total',
    title: 'Total Answers / Assigns',
    style: {flex: 1},
  },
  // { name: 'totalAnswers', title: 'Total Answers', style: { flex: 1 } },
  {
    name: 'actions',
    title: 'Actions',
    style: {flex: 1},
    cellType: 'oxd-table-cell-actions',
    cellRenderer: generateActions,
  },
]);

onMounted(() => {
  if (isEmployee.value) {
    headers.value[7].cellConfig = {
      view: {
        onClick: onClickView,
        props: {
          name: 'eye',
        },
      },
    };
  }
});
</script>
<template>
  <div class="orangehrm-background-container">
    <slot
      :filters="filters"
      :onReset="onReset"
      :onSubmit="searchWithFilters"
    ></slot>
    <br />
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <div v-if="checkedItems > 0" class="actions">
          <oxd-text tag="span">
            {{ $t('general.n_records_selected', {count: checkedItems.length}) }}
          </oxd-text>
        </div>
        <oxd-text v-else tag="span">
          {{ $t('general.n_records_found', {count: total}) }}
        </oxd-text>
      </div>
      <div
        v-if="!isEmployee"
        class="orangehrm-horizontal-padding orangehrm-vertical-padding"
      >
        <oxd-button
          label="Add"
          size="medium"
          icon-name="plus"
          type="button"
          display-type="secondary"
          :disabled="isEmployee"
          @click="goToCreate"
        />
      </div>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :headers="headers"
          :items="response?.data"
          :selectable="false"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <DeleteConfirmationDialog ref="deleteDialog"></DeleteConfirmationDialog>
  </div>
</template>
<style lang="scss" scoped>
.orangehrm-header-container {
  .actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
  }

  span {
    margin-right: 20px;
  }
}
</style>
